import { useState } from "react";
import { useTranslation } from "react-i18next";

import { POUS } from "../framework/data";
import type { PouId } from "../framework/types";
import type { CohortFilters, ReportAggregates } from "./reportTypes";
import { EntryExitBars } from "./components/EntryExitBars";
import { ChangeAnalysis } from "./components/ChangeAnalysis";
import { pct } from "./format";
import { TransitionMatrix } from "./components/TransitionMatrix";
import { DrillModal, type DrillCategory } from "./DrillModal";
import { useLabels } from "./useLabels";

/**
 * Te Pou view — the Te Pou Mataaho top-level summary (all four Pou compared
 * entry→exit), then a detail section per Pou: roll-up bars, analysis of change,
 * and the entry×exit transition matrix for each of the Pou's subdomains. Mirrors
 * the four Te Pou sheets plus the Te Pou Mataaho view sheet.
 */
type Props = { agg: ReportAggregates; filters: CohortFilters };

type DrillTarget = {
  subdomainId: string;
  label: string;
  category: DrillCategory;
};

export function TePouView({ agg, filters }: Props) {
  const { t } = useTranslation();
  const labels = useLabels();
  const pouAgg = (id: PouId) => agg.byPou.find((p) => p.pouId === id);
  const [drill, setDrill] = useState<DrillTarget | null>(null);

  return (
    <div className="rpt-view">
      <section className="rpt-panel">
        <h2 className="rpt-panel__title" lang="mi">
          Te Pou Mataaho
        </h2>
        <p className="rpt-panel__lead">{t("reports.pou.overviewLead")}</p>
        <div className="rpt-pou-grid">
          {POUS.map((pou) => {
            const p = pouAgg(pou.id);
            if (!p) return null;
            const tot = p.analysis.total;
            return (
              <div key={pou.id} className="rpt-pou-card">
                <h3 className="rpt-pou-card__name" lang="mi">
                  {pou.nameMi}
                </h3>
                <EntryExitBars data={p} compact />
                <p
                  className="rpt-pou-card__change"
                  style={{ fontSize: "0.8rem", margin: "6px 0 0" }}
                >
                  {tot > 0 ? (
                    <>
                      <span style={{ color: "var(--state-protective)" }}>
                        ▲ {pct(p.analysis.improved / tot)} improved
                      </span>
                      {" · "}
                      <span style={{ color: "var(--state-risk)" }}>
                        ▼ {pct(p.analysis.regressed / tot)} regressed
                      </span>
                    </>
                  ) : (
                    <span style={{ color: "var(--ink-muted)" }}>
                      No change data yet
                    </span>
                  )}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {POUS.map((pou) => {
        const p = pouAgg(pou.id);
        if (!p) return null;
        const subs = agg.bySubdomain.filter(
          (s) => s.pouId === pou.id && s.transition.total > 0,
        );
        return (
          <section key={pou.id} className="rpt-panel">
            <h2 className="rpt-panel__title rpt-panel__title--pou" lang="mi">
              {pou.nameMi}
            </h2>
            <p className="rpt-panel__desc">{labels.pouDescription(pou.id)}</p>

            <div className="rpt-pou-detail">
              <div className="rpt-pou-detail__rollup">
                <h3 className="rpt-subhead">{t("reports.pou.rollup")}</h3>
                <EntryExitBars data={p} />
              </div>
              <div className="rpt-pou-detail__change">
                <h3 className="rpt-subhead">{t("reports.analysisOfChange")}</h3>
                <ChangeAnalysis analysis={p.analysis} />
              </div>
            </div>

            <h3 className="rpt-subhead">{t("reports.pou.transitions")}</h3>
            <div className="rpt-matrix-grid">
              {subs.map((s) => {
                const label = labels.subdomain(s.subdomainId);
                const cats: { c: DrillCategory; n: number }[] = [
                  { c: "regressed", n: s.analysis.regressed },
                  { c: "unchanged", n: s.analysis.remained },
                  { c: "improved", n: s.analysis.improved },
                ];
                return (
                  <div key={s.subdomainId} className="rpt-matrix-cell">
                    <TransitionMatrix
                      transition={s.transition}
                      caption={label}
                    />
                    <div
                      className="rpt-drill-actions"
                      role="group"
                      aria-label={`${t("reports.drill.interrogate")}: ${label}`}
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        marginTop: "6px",
                      }}
                    >
                      {cats.map(({ c, n }) => (
                        <button
                          key={c}
                          type="button"
                          className="rpt-drill-btn"
                          disabled={n === 0}
                          onClick={() =>
                            setDrill({
                              subdomainId: s.subdomainId,
                              label,
                              category: c,
                            })
                          }
                          style={{
                            minHeight: "32px",
                            padding: "2px 10px",
                            borderRadius: "8px",
                            border: "1px solid var(--border, #d4d9e0)",
                            background: "var(--surface, #fff)",
                            fontSize: "0.8rem",
                            cursor: n === 0 ? "default" : "pointer",
                            opacity: n === 0 ? 0.5 : 1,
                          }}
                        >
                          {t(`reports.drill.category.${c}`)} ({n})
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {drill && (
        <DrillModal
          subdomainId={drill.subdomainId}
          subdomainLabel={drill.label}
          category={drill.category}
          filters={filters}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}

export default TePouView;
